import { CognitoIdentityProviderClient, AdminGetUserCommand, ListUsersCommand, AdminCreateUserCommand, AdminUpdateUserAttributesCommand, AdminDeleteUserCommand, RespondToAuthChallengeCommand, InitiateAuthCommand, AdminGetUserCommandInput, AdminGetUserCommandOutput } from "@aws-sdk/client-cognito-identity-provider";

import awsConfig from '../amplifyconfiguration.json'
// IM_ACCESS_KEY and IAM_ACCESS_SECRET_KEY removed as we use awsClients
import { CONFIRMED, COUNTRY_NAME_INDEX, HOSPITAL, HOSPITAL_ID_CREATED_AT_INDEX, INDIVIDUAL, NURSE, ROLE_CREATED_AT_INDEX, STATUS_INDEX, TENANT_ID_CREATED_AT_INDEX, UNCONFIRMED, WAITING, WAITING_FOR_APPROVAL } from "@constants/constants";
import { DynamoDBClient, PutItemCommand, ScanCommand, Select, GetItemCommand, UpdateItemCommand, UpdateItemCommandInput, QueryCommand, QueryCommandInput, QueryCommandOutput } from '@aws-sdk/client-dynamodb';
import {
  S3Client,
  GetObjectCommand,
  DeleteObjectCommand
} from '@aws-sdk/client-s3';
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { capitalizeFirstLetter, formatDateDMY, generateUniqueTimestamp, getDataByDeviceId, getDataUsingDeviceId, getDataUsingHospitalId, getDataUsingTenantId, getDataUsingUserId } from "./sharedUtils";
import { HOSPITAL_DEVICE_TABLE, HOSPITALS_TABLE, NURSE_DEVICE_TABLE, USER_INFO_TABLE, DEVICE_TABLE, DEVICE_LIVE_DATA_TABLE, DEVICE_CHANGE_LOG_TABLE, INDIVIDUAL_DEVICE_TABLE } from "@constants/dynamoDBTableName";
import { getDeviceChangeLogDataUsingDeviceId, fetchAnalyticsChartApi } from "./deviceUtils";
export { fetchAnalyticsChartApi };
import { COUNTRIES_API, STATES_API, USER_VERIFICATION_API } from "@constants/auth/Api";
import { API_KEY } from "@constants/AppConfig";

interface UserAttribute {
  Name: string;
  Value: string;
}

import { client, dbClient, s3Client } from './awsClients';

const MAX_COGNITO_USER_FETCH_CONCURRENCY = 5;

async function mapWithConcurrency<T, R>(items: T[], concurrency: number, mapper: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  });

  await Promise.all(workers);
  return results;
}

export const formattedDateTime = generateUniqueTimestamp();

/**
 * Clears localStorage while preserving the user's language preference.
 * Use this instead of localStorage.clear() to keep the selected language
 * across login/logout/navigation flows.
 */
export const clearLocalStoragePreserveLanguage = () => {
    const savedLang = localStorage.getItem('appLanguage');
    localStorage.clear();
    if (savedLang) {
        localStorage.setItem('appLanguage', savedLang);
    }
};

export async function checkUserExists(emailID: string, checkCondition: boolean) {
  return new Promise((resolve, reject) => {
    fetch(USER_VERIFICATION_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({ email: emailID }),
    })
      .then(async (response) => {
        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          // If JSON parsing fails, reject with appropriate error based on status
          if (response.status === 404) {
            // User does not exist
            if (!checkCondition) {
              reject({ name: 'NotAuthorizedException' });
            } else {
              resolve(void 0);
            }
          } else {
            reject({ name: 'InternalErrorException' });
          }
          return;
        }
        
        if (response.status === 200) {
          // User exists
          if (checkCondition) {
            // For signup: user exists, so reject
            reject({ name: 'UsernameExistsException' });
          } else {
            // For login: user exists, so resolve
            resolve(void 0);
          }
        } else if (response.status === 404) {
          // User does not exist
          if (!checkCondition) {
            // For login: user doesn't exist, so reject
            reject({ name: 'NotAuthorizedException' });
          } else {
            // For signup: user doesn't exist, so resolve (can proceed with signup)
            resolve(void 0);
          }
        } else if (response.status === 400) {
          // Handle FORCE_CHANGE_PASSWORD or other 400 errors
          if (data.message && data.message.includes('password needs to be changed')) {
            reject({ name: 'PasswordResetRequiredException' });
          } else {
            reject({ name: 'InvalidParameterException' });
          }
        } else if (response.status === 401) {
          reject({ name: 'NotAuthorizedException' });
        } else if (response.status === 429) {
          reject({ name: 'TooManyRequestsException' });
        } else {
          // Other errors
          reject({ name: 'InternalErrorException' });
        }
      })
      .catch((error) => {
        // Network or other fetch errors
        reject({ name: error.name || 'InternalErrorException' });
      });
  });
}

export async function fetchCountries() {
  try {
    const response = await fetch(COUNTRIES_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    // Handle both array response and object with countries property
    const countriesArray = Array.isArray(data) ? data : (data.countries || []);
    const countries = countriesArray.map((item: any) => ({
      id: item.id,
      code: item.countryCode,
      name: item.name,
    }));
    return { countries };
  } catch (error: any) {
    let errorMessage;
    if (error.message) {
      errorMessage = error.message;
    } else {
      errorMessage = 'An unknown error occurred while fetching countries';
    }
    return { error: errorMessage };
  }
}

export async function fetchStates(countryId: string) {
  try {
    const response = await fetch(STATES_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({ countryCode: countryId }),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    // Handle both array response and object with states property
    const statesArray = Array.isArray(data) ? data : (data.states || []);
    const states = statesArray.map((item: any) => ({
      id: item.id,
      name: item.name,
    }));
    return { states };
  } catch (error: any) {
    console.error("Error fetching states:", error);
    let errorMessage;
    if (error.message) {
      errorMessage = error.message;
    } else {
      errorMessage = "An unknown error occurred while fetching states";
    }
    return { error: errorMessage };
  }
}

export async function fetchCountryId(countryName: string) {
  const params = {
    TableName: "country",
    IndexName: COUNTRY_NAME_INDEX,
    KeyConditionExpression: 'country_name = :country_name',
    ExpressionAttributeValues: {
      ':country_name': { S: countryName }
    },
  };

  try {
    const data = await dbClient.send(new QueryCommand(params));
    if (data && data.Items && data.Items.length > 0) {
      return data.Items
    }
    return null;
  } catch (error: any) {
    console.error("Error fetching states:", error);
    let errorMessage;
    switch (error.name) {
      case "ValidationException":
        errorMessage = "Validation error occurred";
        break;
      case "ResourceNotFoundException":
        errorMessage = "Resource not found";
        break;
      case "RequestLimitExceeded":
        errorMessage = "Request limit exceeded";
        break;
      default:
        errorMessage = error.message || "An unknown error occurred";
    }
    return { error: errorMessage };
  }
}

export async function fetchDeviceCount() {
  const params = {
    TableName: DEVICE_TABLE,
    Select: Select.COUNT,
  };

  try {
    const data = await dbClient.send(new ScanCommand(params));
    const count = data.Count ? data.Count : 0;
    return { count: data.Count };
  } catch (error: any) {
    let errorMessage;
    switch (error.name) {
      case 'ValidationException':
        errorMessage = 'Validation error occurred';
        break;
      case 'ResourceNotFoundException':
        errorMessage = 'Resource not found';
        break;
      case 'RequestLimitExceeded':
        errorMessage = 'Request limit exceeded';
        break;
      default:
        errorMessage = error.message || 'An unknown error occurred';
    }

    return { error: errorMessage };
  }
}
export async function getHospitalCount(tableName: string, hospitalId: string): Promise<QueryCommandOutput> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: HOSPITAL_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'hospital_id = :hospital_id',
    Select: Select.COUNT,
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
      ':deleted_at': { S: '' },
    },
  };

  try {
    const response = await dbClient.send(new QueryCommand(params));
    return response
  } catch (error: any) {
    throw error;
  }
}
export const getAllDeviceIds = async (hospitalId: string) => {
  const params = {
    TableName: HOSPITAL_DEVICE_TABLE,
    FilterExpression: 'hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
    },
  };

  try {
    const data = await dbClient.send(new ScanCommand(params));
    if (data.Items && data.Items.length > 0) {
      return data.Items.map(item => item.device_id.S); // Extract all device IDs
    } else {
      return []; // No devices found for this hospital
    }
  } catch (err) {
    console.error('Error fetching device IDs:', err);
    throw err;
  }
};

export const getAllIndividualDeviceIds = async (tenatnId: string) => {
  const params = {
    TableName: INDIVIDUAL_DEVICE_TABLE,
    FilterExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenatnId },
    },
  };

  try {
    const data = await dbClient.send(new ScanCommand(params));
    if (data.Items && data.Items.length > 0) {
      return data.Items.map(item => item.device_id.S); // Extract all device IDs
    } else {
      return []; // No devices found for this hospital
    }
  } catch (err) {
    console.error('Error fetching device IDs:', err);
    throw err;
  }
};
export async function getHospitalNurseCount(tableName: string, hospitalId: string): Promise<QueryCommandOutput> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: HOSPITAL_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'hospital_id = :hospital_id',
    Select: Select.COUNT,
    FilterExpression: 'deleted_at = :deleted_at AND #role = :role',
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
      ':role': { S: 'Nurse' },
      ':deleted_at': { S: '' },
    },
    ExpressionAttributeNames: {
      '#role': 'role',  // Map the placeholder to the actual attribute name
    },
  };

  try {
    const response = await dbClient.send(new QueryCommand(params));
    return response
  } catch (error: any) {
    throw error;
  }
}
export async function getActiveInfusionCountData(tableName: string, hospitalId: string): Promise<QueryCommandOutput> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: HOSPITAL_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'hospital_id = :hospital_id',
    FilterExpression: 'deleted_at = :deleted_at AND #status = :status',
    ExpressionAttributeNames: {
      '#status': 'status' // Using ExpressionAttributeNames to avoid reserved word conflicts
    },
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
      ':deleted_at': { S: '' },
      ':status': { S: 'infusion progress' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response;

  } catch (error: any) {
    throw error;
  }
}
// Helper function to get the week number from a date
function getWeekNumber(date: Date): number {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}
type GroupedInfusionData = Map<number, Array<any>>;
export async function getCompletedInfusionCountData(tableName: string, hospitalId: string): Promise<GroupedInfusionData> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: HOSPITAL_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'hospital_id = :hospital_id',
    FilterExpression: 'deleted_at = :deleted_at AND #status = :status',
    ExpressionAttributeNames: {
      '#status': 'status' // Using ExpressionAttributeNames to avoid reserved word conflicts
    },
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
      ':deleted_at': { S: '' },
      ':status': { S: 'infusion completed' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    // Extract the Items array from the response
    const items = response.Items || [];

    // Group by week
    const groupedData = items.reduce((acc: Map<number, Array<any>>, item) => {
      const createdAt = item.created_at?.S;
      if (createdAt) {
        const date = new Date(createdAt);
        const weekNumber = getWeekNumber(date);

        if (!acc.has(weekNumber)) {
          acc.set(weekNumber, []);
        }

        // Push the item into the corresponding week group
        acc.get(weekNumber)?.push(item);
      }

      return acc;
    }, new Map<number, Array<any>>());

    return groupedData;
  } catch (error: any) {
    throw error;
  }
}

export async function getCompletedInfusionCountDataUsingTenant(tableName: string, tenantId: string): Promise<GroupedInfusionData> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    FilterExpression: 'deleted_at = :deleted_at AND #status = :status',
    ExpressionAttributeNames: {
      '#status': 'status' // Using ExpressionAttributeNames to avoid reserved word conflicts
    },
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId },
      ':deleted_at': { S: '' },
      ':status': { S: 'infusion completed' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    // Extract the Items array from the response
    const items = response.Items || [];

    // Group by week
    const groupedData = items.reduce((acc: Map<number, Array<any>>, item) => {
      const createdAt = item.created_at?.S;
      if (createdAt) {
        const date = new Date(createdAt);
        const weekNumber = getWeekNumber(date);

        if (!acc.has(weekNumber)) {
          acc.set(weekNumber, []);
        }

        // Push the item into the corresponding week group
        acc.get(weekNumber)?.push(item);
      }

      return acc;
    }, new Map<number, Array<any>>());

    return groupedData;
  } catch (error: any) {
    throw error;
  }
}
export async function fetchUserCount(role: string) {
  const params: QueryCommandInput = {
    TableName: USER_INFO_TABLE,
    Select: 'COUNT',
    IndexName: 'role-created_at-index',
    KeyConditionExpression: '#role = :role',
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeNames: {
      '#role': 'role',
    },
    ExpressionAttributeValues: {
      ':role': { S: role },
      ':deleted_at': { S: '' },
    },
  };

  try {

    const data = await dbClient.send(new QueryCommand(params));

    const count = data.Count !== undefined ? data.Count : 0;
    return { count };
  } catch (error: any) {
    let errorMessage;
    switch (error.name) {
      case 'ValidationException':
        errorMessage = 'Validation error occurred';
        break;
      case 'ResourceNotFoundException':
        errorMessage = 'Resource not found';
        break;
      case 'RequestLimitExceeded':
        errorMessage = 'Request limit exceeded';
        break;
      default:
        errorMessage = error.message || 'An unknown error occurred';
    }

    return { error: errorMessage };
  }
}
export async function allUsersData() {
  const params = {
    TableName: USER_INFO_TABLE,
    FilterExpression: "deleted_at = :deleted_at",
    ExpressionAttributeValues: {
      ":deleted_at": { S: "" }
    }
  };

  try {
    const data = await dbClient.send(new ScanCommand(params));
    const groupedData = groupByDate(data.Items || []);
    return groupedData;
  } catch (error: any) {
    let errorMessage;
    switch (error.name) {
      case 'ValidationException':
        errorMessage = 'Validation error occurred';
        break;
      case 'ResourceNotFoundException':
        errorMessage = 'Resource not found';
        break;
      case 'RequestLimitExceeded':
        errorMessage = 'Request limit exceeded';
        break;
      default:
        errorMessage = error.message || 'An unknown error occurred';
    }
    return { error: errorMessage };
  }
}

export async function groupByDate(items: any[]) {
  return items.reduce((acc: any, item: any) => {
    // Skip items without created_at or S property
    if (!item?.created_at?.S) {
      return acc;
    }

    const createdAt = item.created_at.S;
    const date = new Date(createdAt).toISOString().split("T")[0]; // Extract the date part

    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {});
}


interface UserData {
  id: string;
  email?: string;
  hospitalEmail?: string;
  name?: string;
  mobileNo?: string;
  createdAt?: string;
  createdAt_time?: string;
  hospitalName?: string;
  hospitalContact?: string;
  status?: string;
  role?: string;
  tenantId?: string;
  slug?: string;
  nurseId?: string;
  createdBy?: string;
  userEmailStatus?: string;
  hosUuid?: string;
}

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const transformUser = (userType: any) => {
  const formattedDate = formatDate(userType.UserCreateDate);

  return {
    Attributes: userType.Attributes,
    Enabled: userType.Enabled,
    UserCreateDate: userType.UserCreateDate,
    UserCreateDateString: formattedDate, // Add formatted date string
    UserLastModifiedDate: userType.UserLastModifiedDate,
    UserStatus: userType.UserStatus,
    Username: userType.Username
  };
};

const fetchAllUsers = async () => {
  let allUsers: any = [];
  let paginationToken = null;

  do {
    const input: any = {
      UserPoolId: awsConfig.aws_user_pools_id,
      PaginationToken: paginationToken,
    };

    try {
      const command = new ListUsersCommand(input);
      const response = await client.send(command);
      if (response.Users) {
        allUsers = allUsers.concat(response.Users);
      }

      paginationToken = response.PaginationToken;
    } catch (error) {
      throw error;
    }
  } while (paginationToken);

  return allUsers;
};

export async function userList(role: string): Promise<UserData[]> {
  // export async function userList(role: string) {
  const params = {
    TableName: 'user_info',
    IndexName: ROLE_CREATED_AT_INDEX,
    KeyConditionExpression: '#role = :role',
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeNames: {
      '#role': 'role',
    },
    ExpressionAttributeValues: {
      ':role': { S: role },
      ':deleted_at': { S: '' },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    if (response.Items && response.Items.length > 0) {
      const extractedData = await mapWithConcurrency(response.Items, MAX_COGNITO_USER_FETCH_CONCURRENCY, async (user: any) => {
        const id = user.user_id?.S;
        const email = user.email?.S ? user.email?.S : '--';
        const userCreateDateString = user.created_at?.S ? formatDateDMY(user.created_at?.S) : '--';
        const deviceId = user.device_id?.S ? user.device_id?.S : '--';
        const tenantId = user.tenant_id?.S;
        const userId = user.user_id?.S;
        const mobileNo = user.mobile_number?.S ? user.mobile_number?.S : '--';
        const userName = user.name?.S;
        const data = await getUserData(userId);
        const userStatus = data?.UserStatus ?? undefined;

        if (tenantId) {
          try {
            let userData: UserData = { id, email };

            if (role.toLowerCase() === HOSPITAL.toLowerCase()) {
              const res = await getDataUsingTenantId('hospital_info', tenantId);
              const hosRes = await getDataUsingTenantId('hospitals', tenantId);
              if (res?.Items?.length === 0 && hosRes?.Items?.length === 0) {
                return null;
              }
              const details = res?.Items ? res.Items[0] : null;
              const hosDetails = hosRes?.Items ? hosRes.Items[0] : null;
              userData = {
                id,
                name: userName,
                hospitalName: user?.hospital_name?.S || "",
                email,
                hospitalEmail: details?.hospital_email?.S ? details?.hospital_email?.S : "--",
                hospitalContact: details?.hospital_contact?.S ? details?.hospital_contact?.S : "--",
                status: hosDetails?.status?.S ? capitalizeFirstLetter(hosDetails?.status?.S) : '',
                hosUuid: hosDetails?.uuid?.S ? hosDetails?.uuid?.S : '',
                createdAt: userCreateDateString,
                createdAt_time: user?.created_at?.S ? user?.created_at?.S : '',
                role,
                userEmailStatus: userStatus ? userStatus.toLowerCase() == "confirmed" ? 'verified' : 'not verified' : '--',
                mobileNo,
                tenantId,
                slug: user?.slug?.S || ''
              };
            } else if (role.toLowerCase() === INDIVIDUAL.toLowerCase()) {
              userData = {
                id,
                name: userName,
                email,
                mobileNo,
                createdAt: userCreateDateString,
                createdAt_time: user?.created_at?.S ? user?.created_at?.S : '',
                userEmailStatus: userStatus ? userStatus.toLowerCase() == "confirmed" ? 'verified' : 'not verified' : '--',
                role,
                tenantId,
                slug: user?.slug?.S || ''
              };
            } else if (role.toLowerCase() === NURSE.toLowerCase()) {
              userData = {
                id,
                name: capitalizeFirstLetter(userName),
                email,
                hospitalName: capitalizeFirstLetter(user?.hospital_name?.S ? user?.hospital_name?.S : ''),
                status: capitalizeFirstLetter(user?.status?.S ? user?.status?.S.toLowerCase() == WAITING.toLowerCase() ? WAITING_FOR_APPROVAL : user?.status?.S : ''),
                createdAt: userCreateDateString,
                mobileNo,
                createdAt_time: user?.created_at?.S ? user?.created_at?.S : '',
                createdBy: user?.created_by?.S ? user?.created_by?.S : '',
                nurseId: user?.nurse_id?.S ? user?.nurse_id?.S.toUpperCase() : '',
                userEmailStatus: userStatus ? userStatus.toLowerCase() == "confirmed" ? 'verified' : 'not verified' : '--',
                role,
                tenantId,
                slug: user?.slug?.S || ''
              };
            }

            return userData;
          } catch (error: any) {
            throw error;
          }
        }

        return null; // Skip this user if tenantId is not present
      });
      return extractedData.filter((user): user is UserData => user !== null);
    } else {
      return []
    }
  } catch (error: any) {
    throw error;
  }
}
export async function hospitalUserList(): Promise<UserData[]> {
  const params = {
    TableName: 'hospital_info',
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);

    if (response.Items && response.Items.length > 0) {
      const extractedData = await mapWithConcurrency(response.Items, MAX_COGNITO_USER_FETCH_CONCURRENCY, async (user: any) => {
        const tenantId = user.tenant_id?.S;
        const userRes = await getHospitalData('user_info', tenantId);
        if (userRes?.Items?.length === 0) {
          return null;
        }
        const userDetails = userRes?.Items ? userRes.Items[0] : null;
        if (userDetails == null) {
          return null;
        }
        const id = userDetails.user_id?.S ? userDetails.user_id?.S : '--';
        const email = userDetails.email?.S ? userDetails.email?.S : '--';
        const userCreateDateString = userDetails.created_at?.S ? formatDateDMY(userDetails.created_at?.S) : '--';
        const deviceId = userDetails.device_id?.S ? userDetails.device_id?.S : '--';
        const userId = userDetails.user_id?.S ? userDetails.user_id?.S : '--';
        const mobileNo = userDetails.mobile_number?.S ? userDetails.mobile_number?.S : '--';
        const userName = userDetails.name?.S;
        const data = await getUserData(userId);
        const userStatus = data?.UserStatus ?? undefined;

        if (tenantId) {
          try {
            let userData: UserData = { id, email };
            const hosRes = await getDataUsingTenantId('hospitals', tenantId);
            if (hosRes?.Items?.length === 0) {
              return null;
            }
            const hosDetails = hosRes?.Items ? hosRes.Items[0] : null;
            userData = {
              id,
              name: userName,
              hospitalName: user?.hospital_name?.S || "",
              email,
              hospitalEmail: user?.hospital_email?.S ? user?.hospital_email?.S : "--",
              hospitalContact: user?.hospital_contact?.S ? user?.hospital_contact?.S : "--",
              status: hosDetails?.status?.S ? capitalizeFirstLetter(hosDetails?.status?.S) : '',
              hosUuid: hosDetails?.uuid?.S ? hosDetails?.uuid?.S : '',
              createdAt: userCreateDateString,
              createdAt_time: user?.created_at?.S ? user?.created_at?.S : '',
              userEmailStatus: userStatus ? userStatus.toLowerCase() == "confirmed" ? 'verified' : 'not verified' : '--',
              mobileNo,
              tenantId,
              role: HOSPITAL,
              slug: userDetails?.slug?.S || ''
            };
            return userData;
          } catch (error: any) {
            throw error;
          }
        }

        return null; // Skip this user if tenantId is not present
      });
      return extractedData.filter((user): user is UserData => user !== null);
    } else {
      return []
    }
  } catch (error: any) {
    throw error;
  }
}

export async function hospitalUserWaitingList(role: string): Promise<UserData[]> {
  const params = {
    TableName: HOSPITALS_TABLE,
    IndexName: STATUS_INDEX,
    KeyConditionExpression: '#status = :status',
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeNames: {
      '#status': 'status',
    },
    ExpressionAttributeValues: {
      ':status': { S: WAITING },
      ':deleted_at': { S: '' },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    if (response.Items && response.Items.length > 0) {
      const extractedData = await Promise.all(response.Items.map(async (user: any) => {
        const tenantId = user.tenant_id?.S;
        const res = await getDataUsingTenantId('hospital_info', tenantId);
        const hosRes = await getDataUsingTenantId('hospitals', tenantId);
        const userRes = await getHospitalData('user_info', tenantId);

        if (res?.Items?.length === 0 && hosRes?.Items?.length === 0 && userRes?.Items?.length === 0) {
          return null;
        }

        const details = res?.Items ? res.Items[0] : null;
        const hosDetails = hosRes?.Items ? hosRes.Items[0] : null;
        const userDetails = userRes?.Items ? userRes.Items[0] : null;

        if (details == null || hosDetails == null || userDetails == null) {
          return null;
        }

        const id = userDetails.user_id?.S ? userDetails.user_id?.S : '';
        const email = userDetails.email?.S ? userDetails.email?.S : '--';
        const userCreateDateString = userDetails.created_at?.S ? formatDateDMY(userDetails.created_at?.S) : '--';
        const deviceId = userDetails.device_id?.S ? userDetails.device_id?.S : '--';

        const userId = userDetails.user_id?.S;
        const mobileNo = userDetails.mobile_number?.S ? userDetails.mobile_number?.S : '--';
        const userName = userDetails.name?.S;

        if (tenantId) {
          try {
            let userData: UserData = { id, email };

            if (role.toLowerCase() === HOSPITAL.toLowerCase()) {

              const details = res?.Items ? res.Items[0] : null;
              const hosDetails = hosRes?.Items ? hosRes.Items[0] : null;
              userData = {
                id,
                name: userName,
                hospitalName: details?.hospital_name?.S || "",
                email,
                hospitalEmail: details?.hospital_email?.S ? details?.hospital_email?.S : "--",
                hospitalContact: details?.hospital_contact?.S ? details?.hospital_contact?.S : "--",
                status: hosDetails?.status?.S ? capitalizeFirstLetter(hosDetails?.status?.S) : '',
                hosUuid: hosDetails?.uuid?.S ? hosDetails?.uuid?.S : '',
                createdAt: userCreateDateString,
                createdAt_time: user?.created_at?.S ? user?.created_at?.S : '',
                role,
                userEmailStatus: '--',
                mobileNo,
                tenantId,
                slug: user?.slug?.S || ''
              };
            }

            return userData;
          } catch (error: any) {
            throw error;
          }
        }

        return null; // Skip this user if tenantId is not present
      }));
      return extractedData
        .filter((user): user is UserData => user !== null)
        .sort((a, b) => {
          if (a.createdAt_time && b.createdAt_time) {
            return new Date(b.createdAt_time).getTime() - new Date(a.createdAt_time).getTime();
          }
          return 0;
        });
    } else {
      return []
    }
  } catch (error: any) {
    throw error;
  }
}

async function getHospitalData(tableName: string, tenantId: string): Promise<QueryCommandOutput> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: TENANT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'tenant_id = :tenant_id',
    FilterExpression: "deleted_at = :deleted_at AND #role = :role",
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId },
      ':role': { S: HOSPITAL },
      ':deleted_at': { S: '' }
    },
    ExpressionAttributeNames: {
      "#role": "role"
    }
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    throw error;
  }
}


async function scanHospitalInfo(tableName: string, tenantId: string) {
  const params = {
    TableName: tableName,
    FilterExpression: "#tenantId = :tenantId AND #deleted_at = :deleted_at",
    ExpressionAttributeNames: {
      "#tenantId": "tenant_id",
      "#deleted_at": "deleted_at"
    },
    ExpressionAttributeValues: {
      ":tenantId": { S: tenantId },
      ":deleted_at": { S: '' }
    },
  };

  try {
    const response = await getDataUsingTenantId(tableName, tenantId);
    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

async function getUserInfoFromCognito(id: string) {
  const params = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: id,
  };

  try {
    const command = new AdminGetUserCommand(params);
    const response = await client.send(command);
    return response;
  } catch (error) {
    console.error('Error getting user info from Cognito:', error);
    throw error;
  }
}

export async function getNurseList(tableName: string, hospitalId: string, tenantId: string) {
  const params = {
    TableName: 'user_info',
    IndexName: TENANT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'tenant_id = :tenant_id',
    FilterExpression: 'deleted_at = :deleted_at AND #role = :role AND #hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ":tenant_id": { S: tenantId },
      ':role': { S: NURSE },
      ':deleted_at': { S: '' },
      ':hospital_id': { S: hospitalId },
    },
    ExpressionAttributeNames: {
      '#role': 'role',
      '#hospital_id': 'hospital_id',
    },
  };
  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    if (response.Items && response.Items.length > 0) {
      const extractedData = await mapWithConcurrency(response.Items, MAX_COGNITO_USER_FETCH_CONCURRENCY, async (user: any) => {
        const id = user.user_id?.S;
        const email = user.email?.S ? user.email?.S : '--';
        const userCreateDateString = user.created_at?.S ? formatDateDMY(user.created_at?.S) : '--';
        const deviceId = user.device_id?.S ? user.device_id?.S : '--';
        const tenantId = user.tenant_id?.S;
        const userId = user.user_id?.S;
        const mobileNo = user.mobile_number?.S ? user.mobile_number?.S : '--';
        const userName = user.name?.S;
        const data = await getUserData(userId);
        const userStatus = data?.UserStatus ?? undefined;

        if (tenantId) {
          try {
            let userData: UserData = { id, email };

            userData = {
              id,
              name: capitalizeFirstLetter(userName),
              nurseId: user?.nurse_id?.S ? user?.nurse_id?.S.toUpperCase() : '',
              email,
              hospitalName: capitalizeFirstLetter(user?.hospital_name?.S ? user?.hospital_name?.S : ''),
              status: capitalizeFirstLetter(user?.status?.S ? user?.status?.S.toLowerCase() == WAITING.toLowerCase() ? WAITING_FOR_APPROVAL : user?.status?.S : ''),
              createdAt: userCreateDateString,
              mobileNo,
              createdAt_time: user?.created_at?.S ? user?.created_at?.S : '',
              createdBy: user?.created_by?.S ? user?.created_by?.S : '',
              userEmailStatus: userStatus ? userStatus.toLowerCase() == "confirmed" ? 'verified' : 'not verified' : '--',
              role: NURSE.toLowerCase(),
              tenantId,
              slug: user?.slug?.S || ''
            };

            return userData;
          } catch (error: any) {
            throw error;
          }
        }

        return null; // Skip this user if tenantId is not present
      });
      return extractedData.filter((user): user is UserData => user !== null);
    } else {
      return []
    }
  } catch (error) {
    console.error('Error getting user info:', error);
    throw error;
  }
}

export async function getUserInfo(tableName: string, tenantId: string,) {

  try {
    const response = await getDataUsingTenantId(tableName, tenantId);
    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function getUserIDInfo(tableName: string, tenantId: string,) {
  const params = {
    TableName: tableName,
    FilterExpression: "#user_id = :user_id",
    ExpressionAttributeNames: {
      "#user_id": "user_id",
    },
    ExpressionAttributeValues: {
      ":user_id": { S: tenantId },
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function handleFetchLogo(fileName: string) {
  if (!fileName) {
    return;
  }

  const params = {
    Bucket: 'dripsetapplicationstaging',
    Key: fileName,
  };

  try {
    const command = new GetObjectCommand(params);
    const data = await s3Client.send(command);
    const url = await getSignedUrl(s3Client, command, { expiresIn: 3600 });
    return url;
  } catch (error) {
    console.error('Error fetching file:', error);
  }
};

function generatePassword() {
  const length = 8;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+[]{}|;:,.<>?";

  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }

  // Ensure password meets criteria
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()-_=+[\]{}|;:,.<>?]/.test(password);

  if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
    // If criteria not met, recursively call the function until it does
    return generatePassword();
  }

  return password;
}

export const fetchAndFilterUsers = async () => {
  let totalCount = 0;
  let individualCount = 0;
  let hospitalCount = 0;
  let nurseCount = 0;
  let paginationToken: string | null = null; // Ensure paginationToken starts as null

  do {
    const input: any = {
      UserPoolId: awsConfig.aws_user_pools_id,
      PaginationToken: paginationToken ?? undefined, // Only include PaginationToken if it's not null
    };

    try {
      const command = new ListUsersCommand(input);
      const response = await client.send(command);

      if (response.Users) {
        // Filter users based on confirmed status and roles
        const filteredUsers = response.Users.filter((user: any) => {
          const statusAttribute = user.UserStatus; // Accessing UserStatus directly
          const roleAttribute = user.Attributes.find((attr: any) => attr.Name === "custom:role");

          // Check if user is confirmed and role is either Hospital or Individual
          const isConfirmed = statusAttribute === "CONFIRMED"; // Assuming UserStatus is a string
          const isRoleValid = roleAttribute && ["Individual", "Hospital", "Nurse"].includes(roleAttribute.Value);

          return isConfirmed && isRoleValid;
        });

        // Count users based on roles
        filteredUsers.forEach((user: any) => {
          const roleAttribute = user.Attributes.find((attr: any) => attr.Name === "custom:role");
          if (roleAttribute) {
            if (roleAttribute.Value === "Individual") {
              individualCount++;
            } else if (roleAttribute.Value === "Hospital") {
              hospitalCount++;
            } else if (roleAttribute.Value === "Nurse") {
              nurseCount++;
            }
          }
        });

        totalCount += filteredUsers.length;
      }

      paginationToken = response.PaginationToken || null; // Ensure paginationToken is not undefined
    } catch (error) {
      throw error;
    }
  } while (paginationToken);

  return { totalCount, individualCount, hospitalCount, nurseCount };
};

export async function adminCreateUser(name: string, userData: any, password: string) {
  let input;
  if (password == '') {
    input = {
      UserPoolId: awsConfig.aws_user_pools_id,
      Username: name,
      UserAttributes: userData,
      TemporaryPassword: generatePassword(),
    };
  } else {
    input = {
      UserPoolId: awsConfig.aws_user_pools_id,
      Username: name,
      UserAttributes: userData,
      TemporaryPassword: password
    };
  }

  try {
    const command = new AdminCreateUserCommand(input);
    const response = await client.send(command);

    const userAttributes = response.User?.Attributes;
    const userId = userAttributes?.find(attr => attr.Name === 'sub')?.Value;

    return { id: userId }
  } catch (error: any) {
    throw error;
  }
}

export async function adminUpdateUser(name: string, userData: any) {
  const input = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: name,
    UserAttributes: userData
  };

  try {
    const command = new AdminUpdateUserAttributesCommand(input);
    const response = await client.send(command);
  } catch (error) {
    console.error('Error Editing user attributes:', error);
  }

}

export async function updateUser(tableName: string, id: string, attrabutiesNames: any, attributValues: any, UpdateExpression: string) {
  const input: UpdateItemCommandInput = {
    TableName: tableName, // Replace with your table name
    Key: {
      uuid: { S: id }, // Replace with your primary key
    },
    ExpressionAttributeNames: attrabutiesNames,
    ExpressionAttributeValues: attributValues,
    UpdateExpression: UpdateExpression,
    ReturnValues: "ALL_NEW"
  };

  const command = new UpdateItemCommand(input);

  try {
    const response = await dbClient.send(command);
    return {
      statusCode: 200,
      body: JSON.stringify({ message: "Item updated successfully", updatedItem: response.Attributes }),
    };
  } catch (error: any) {
    console.error("Error updating item:", error);
    return {
      statusCode: 500,
      body: JSON.stringify({ message: `Failed to update item: ${error.message}` }),
    };
  }
};

export async function adminDeleteUser(name: string) {
  const input = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: name,
  };

  try {
    const command = new AdminDeleteUserCommand(input);
    const response = await client.send(command);
  } catch (error) {
    console.error('Error Deleting user:', error);
  }

}

export async function deleteS3Object(objectKey: string) {
  const params = {
    Bucket: 'dripsetapplicationstaging',
    Key: objectKey,
  };

  try {
    const command = new DeleteObjectCommand(params);
    const response = await s3Client.send(command);
    return response;
  } catch (error) {
    console.error('Error deleting object:', error);
    throw error;
  }
}

export async function userNewPassword(email: string, password: string, session: string) {
  try {
    const challengeCommand = new RespondToAuthChallengeCommand({
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: awsConfig.aws_user_pools_web_client_id,
      ChallengeResponses: {
        USERNAME: email,
        NEW_PASSWORD: password,
      },
      Session: session,
    });

    const res = await client.send(challengeCommand);
  } catch (error: any) {
    console.error('Error:', error);
    switch (error.name) {
      case 'InvalidParameterException':
        console.error('Invalid parameters provided.');
        break;
      case 'NotAuthorizedException':
        console.error('Incorrect username or password.');
        break;
      case 'CodeMismatchException':
        console.error('Invalid verification code provided.');
        break;
      case 'ExpiredCodeException':
        console.error('Verification code expired.');
        break;
      case 'UserNotFoundException':
        console.error('User does not exist.');
        break;
      default:
        console.error(`An unexpected error occurred: ${error.message}`);
    }
  }
};

export async function getUserData(userId: string): Promise<AdminGetUserCommandOutput | null> {
  const params: AdminGetUserCommandInput = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: userId,
  };
  try {
    const command = new AdminGetUserCommand(params);
    const userData = await client.send(command);
    return userData;
  } catch (error: any) {
    if (error.name === 'UserNotFoundException' || error.__type === 'UserNotFoundException') {
      return null;
    }
    console.error('Error fetching user data:', error);
    throw error;
  }
};

interface HospitalItem {
  uuid: string;
  tenant_id: string;
}

interface HospitalDeviceItem {
  uuid: string;
  tenant_id: string;
  device_id: string;
}


export async function deviceList(tenantId: string) {
  try {
    // Step 1: Fetch UUID from Hospital Table using GSI index query
    const hospitalData = await getDataUsingTenantId(HOSPITALS_TABLE, tenantId);

    if (!hospitalData.Items || hospitalData.Items.length === 0) {
      return [];
    }

    const hospitalItem = hospitalData.Items[0];
    const hospitalUuid = hospitalItem.uuid?.S;


    // Step 2: Fetch hospital device mappings using GSI index query
    const hospitalDeviceData = await getDataUsingHospitalId(HOSPITAL_DEVICE_TABLE, hospitalUuid || '');
    if (!hospitalDeviceData.Items || hospitalDeviceData.Items.length === 0) {
      return [];
    }


    const deviceIds = hospitalDeviceData.Items;

    const deviceList: any[] = [];

    for (const deviceId of deviceIds) {
      const getItemParams = {
        TableName: 'devices',
        Key: {
          'uuid': { S: deviceId.device_id?.S ? deviceId.device_id?.S : '' }
        }
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

      if (deviceData.Item) {
        deviceList.push(deviceData.Item);
      }
    }

    const deviceDetails = [];
    if (deviceList.length != 0) {
      for (const device of deviceList) {
        deviceDetails.push({
          id: device.uuid?.S,
          device_name: capitalizeFirstLetter(device.device_name?.S ? device.device_name?.S : ''),
          device_id: capitalizeFirstLetter(device.device_id?.S ? device.device_id?.S : ''),
          device_status: capitalizeFirstLetter(device.device_status?.S ? device.device_status?.S : ''),
          status: capitalizeFirstLetter(device.status?.S ? device.status?.S : ''),
          created_at: device.created_at?.S ? formatDateDMY(device.created_at?.S) : '',
          created_at_time: device.created_at?.S ? device.created_at?.S : '',
          created_by: device.created_by?.S ? device.created_by?.S : '',
          hospital_block_id: device.hospital_block_id?.S ? device.hospital_block_id?.S : '',
        });
      }
    }
    return deviceDetails;

  } catch (error) {
    console.error('Error fetching device list:', error);
    throw error;
  }

}

export async function individualDeviceList(tenantId: string) {
  try {
    const deviceParams = {
      TableName: 'individual_device',
      IndexName: TENANT_ID_CREATED_AT_INDEX,
      KeyConditionExpression: '#tenant_id = :tenant_id',
      FilterExpression: 'deleted_at = :deleted_at',
      ExpressionAttributeNames: {
        '#tenant_id': 'tenant_id'
      },
      ExpressionAttributeValues: {
        ':tenant_id': { S: tenantId },
        ':deleted_at': { S: '' }
      }
    };

    const deviceData = await dbClient.send(new QueryCommand(deviceParams));
    if (!deviceData.Items || deviceData.Items.length === 0) {
      return [];
    }


    const deviceIds = deviceData.Items.map(item => item);

    if (deviceIds.length === 0) {
      return [];
    }

    const deviceList: any[] = [];

    for (const deviceId of deviceIds) {
      const getItemParams = {
        TableName: 'devices',
        Key: {
          'uuid': { S: deviceId.device_id?.S ? deviceId.device_id?.S : '' }
        }
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

      if (deviceData.Item) {
        deviceList.push(deviceData.Item);
      }
    }

    const deviceDetails = [];
    if (deviceList.length != 0) {
      for (const device of deviceList) {
        deviceDetails.push({
          id: device.uuid?.S,
          device_name: capitalizeFirstLetter(device.device_name?.S ? device.device_name?.S : ''),
          device_id: capitalizeFirstLetter(device.device_id?.S ? device.device_id?.S : ''),
          device_status: capitalizeFirstLetter(device.device_status?.S ? device.device_status?.S : ''),
          status: capitalizeFirstLetter(device.status?.S ? device.status?.S : ''),
          created_at: device.created_at?.S ? formatDateDMY(device.created_at?.S) : '',
          created_at_time: device.created_at?.S ? device.created_at?.S : '',
          created_by: device.created_by?.S ? device.created_by?.S : '',
          hospital_block_id: '',
        });
      }
    }
    return deviceDetails;

  } catch (error) {
    console.error('Error fetching device list:', error);
    throw error;
  }

}

export async function getUserName(tableName: string, tenantId: string, columnName: string) {
  const params = {
    TableName: tableName,
    FilterExpression: "#value = :value",
    ExpressionAttributeNames: {
      "#value": columnName,
    },
    ExpressionAttributeValues: {
      ":value": { S: tenantId },
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

interface DeviceCardDetail {
  id: string;
  nurse_id?: string;
  hasControl?: boolean;
  device_id?: string;
  device_name?: string;
  patient_name?: string;
  medicine_name: string;
  assigned?: string;
  deviceStatus: string;
  infusion_rate: number;
  volume: number;
  volume_consumed: number;
  time_duration: number;
  battery_percentage: number;
  battery_status: string;
  T_L_Time: number;
  T_Qua: number;
  Sel_Qua: number;
  RPM: number;
  error_code: string;
  status: string;
  created_at: string;
  created_at_time: string;
  created_by: string;
  bed_no: string;
}

const infusionStatusMap: Record<string, string> = {
  "0": "Infusion Not Started",
  "1": "Infusion In Progress",
  "2": "Infusion Completed",
  "5": "Infusion Started"
};

const infusionErrorMap: Record<string, string> = {
  "1": "Value Error",
  "2": "Battery less than 15%",
  "4": "Empty Saline bottle",
  "8": "Bubble Detection",
  "16": "Motor Not Working",
  "32": "Over flow detection",
  "64": "No Flow detection",
  "128": "Flow paused"
};


export async function deviceCardList(tenantId: string) {
  try {
    const hospitalDatas = await getDataUsingTenantId(HOSPITALS_TABLE, tenantId);
    if (!hospitalDatas.Items || hospitalDatas.Items.length === 0) {
      return []
    }
    const hospitalTableUuid = hospitalDatas.Items && hospitalDatas.Items.length > 0 ?
      hospitalDatas.Items[0].uuid?.S :
      '';
    const hospitalDeviceData = await getDataUsingHospitalId(HOSPITAL_DEVICE_TABLE, hospitalTableUuid || '');
    if (!hospitalDeviceData.Items || hospitalDeviceData.Items.length === 0) {
      return []
    }

    const deviceList = [];
    const deviceDetails = [];

    for (const device of hospitalDeviceData.Items) {
      const getItemParams = {
        TableName: 'devices',
        Key: {
          'uuid': { S: device.device_id?.S ? device.device_id?.S : '' }
        }
      };
      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

    if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === 'approved') {
      // return deviceData;
      if (deviceData.Item && deviceData.Item?.uuid?.S) {
        const deviceControlData = await getDataByDeviceId(DEVICE_LIVE_DATA_TABLE, deviceData.Item?.device_id?.S || '');
        if (!deviceControlData) {
          continue; // Skip this iteration and continue with the next item
        }

        const deviceChangeLogDatas = await getDeviceChangeLogDataUsingDeviceId(DEVICE_CHANGE_LOG_TABLE, deviceData.Item?.device_id?.S || '');

        const nurseDeviceDatas = await getDataUsingDeviceId(NURSE_DEVICE_TABLE, deviceData.Item?.uuid?.S || '');
        
        const nurseDevice = nurseDeviceDatas && nurseDeviceDatas.Items && nurseDeviceDatas.Items.length > 0 ? nurseDeviceDatas.Items.find(item => item.has_control?.BOOL === true) || nurseDeviceDatas.Items[0] : '';
        const nurseUserData = nurseDevice ? await getDataUsingUserId(USER_INFO_TABLE, nurseDevice.user_id?.S || '') : '';
        const nurseUserItem = nurseUserData ? nurseUserData.Items && nurseUserData.Items.length > 0 ? nurseUserData.Items[0] : '' : '';
        const nurseName = nurseUserItem ? nurseUserItem.name?.S : '';
                
        if (deviceControlData) {
          const deviceControl = deviceControlData;
          deviceDetails.push({
            id: deviceData.Item?.uuid?.S ? deviceData.Item?.uuid?.S : '',
            nurse_id: nurseUserItem && nurseDevice ? nurseDevice.user_id?.S : '',
            hasControl: nurseDevice && nurseDevice.has_control?.BOOL == true ? true : false,
            device_id: deviceData.Item?.device_id?.S ? deviceData.Item?.device_id?.S : '',
            device_name: capitalizeFirstLetter(deviceData.Item?.device_name?.S ? deviceData.Item?.device_name?.S : ''),
            patient_name: deviceChangeLogDatas && deviceChangeLogDatas.patient_name?.S ? capitalizeFirstLetter(deviceChangeLogDatas.patient_name?.S) : '',
            medicine_name: deviceChangeLogDatas && deviceChangeLogDatas.medicine_name?.S ? capitalizeFirstLetter(deviceChangeLogDatas.medicine_name?.S) : '',
            assigned: nurseDevice && nurseDevice.has_control?.BOOL == true ? capitalizeFirstLetter(nurseName || '') : '',
            deviceStatus: capitalizeFirstLetter(deviceData.Item?.device_status?.S ? deviceData.Item?.device_status?.S : ''),
            infusion_rate: deviceControl?.RPM?.N ? parseFloat(deviceControl?.RPM?.N) : 0,
            volume: deviceControl?.Re_saline?.N ? parseFloat(deviceControl?.Re_saline?.N) : 0,
            volume_consumed: deviceControl?.Consume?.N ? parseFloat(deviceControl?.Consume?.N) : 0,
            time_duration: deviceControl?.Time?.N ? parseFloat(deviceControl?.Time?.N) : 0,
            battery_percentage: deviceControl?.Battery?.N ? parseFloat(deviceControl?.Battery?.N) : 0,
            battery_status: (deviceControl?.Battery_Status?.N || deviceControl?.Battery_status?.N || "0"),
            T_L_Time: deviceChangeLogDatas ? deviceChangeLogDatas.T_L_Time?.N : 0,
            T_Qua: deviceChangeLogDatas ? deviceChangeLogDatas.T_Qua?.N : 0,
            Sel_Qua: deviceControl ? deviceControl.Sel_Qua?.N : 0,
            RPM: deviceChangeLogDatas ? deviceChangeLogDatas.RPM?.N : 0,
            error_code: deviceControl && deviceControl.Error_Code?.N && deviceControl.Error_Code?.N != '0' ? infusionErrorMap[deviceControl.Error_Code.N] : '',
            status: deviceControl && deviceControl.Infuse_state?.N
              ? infusionStatusMap[deviceControl.Infuse_state.N] || "Unknown Status"
              : "",
            created_at: deviceData.Item?.created_at?.S ? formatDateDMY(deviceData.Item?.created_at?.S) : '',
            created_at_time: deviceData.Item?.created_at?.S ? deviceData.Item?.created_at?.S : '',
            created_by: deviceData.Item?.created_by?.S ? deviceData.Item?.created_by?.S : '',
            bed_no: deviceData.Item?.hospital_block_id?.S || '',
          });
        }
      }
    } else {
      // Omit the device if not approved
        continue;
      }
    }

    const deviceCardList = (await Promise.all(deviceDetails)).flat().filter(detail => detail !== null && detail !== undefined);
    return deviceCardList as DeviceCardDetail[];

  } catch (error) {
    console.error('Error fetching device list:', error);
    throw error;
  }
}


export async function addDevice(tableName: string, data: any) {
  const params = {
    TableName: tableName,
    Item: data
  };

  try {
    const data = await dbClient.send(new PutItemCommand(params));
  } catch (err) {
    console.error("Error", err);
  }
};
